import { playerToSlug, compareUrl } from "@/lib/playerSlug";
import PublicNav from "@/components/PublicNav";
import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Football, GoogleLogo, ArrowLeft, TrendUp, TrendDown, ArrowRight, Lightning,
  CaretUp, CaretDown, Minus, ArrowsLeftRight, ChartLineUp, Users, Trophy,
  Fire, Scales, Brain, Newspaper, UserCircle, Lock, Crosshair,
  ChartLine, Target, Strategy, GameController
} from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import axios from "axios";
import { API, trackEvent } from "@/App";
import SEO, { BreadcrumbLd, FaqLd } from "@/components/SEO";

const POS_COLORS = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };

function PosBadge({ pos }) {
  const cls = `pos-bg-${(pos || "").toLowerCase()} pos-${(pos || "").toLowerCase()}`;
  return <span className={`inline-flex items-center justify-center px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${cls}`}>{pos}</span>;
}

function StatBox({ label, value, sub, highlight }) {
  return (
    <div className={`border ${highlight ? "border-[#ccff00]/30 bg-[#ccff00]/5" : "border-[#262626] bg-[#171717]"} p-3 sm:p-4`}>
      <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.15em] text-[#555] mb-1">{label}</p>
      <p className={`font-mono font-bold text-lg sm:text-xl ${highlight ? "text-[#ccff00]" : "text-white"}`}>{value}</p>
      {sub && <p className="text-[10px] text-[#666] mt-0.5">{sub}</p>}
    </div>
  );
}

function TrendBadge({ value }) {
  if (!value || value === 0) return <span className="inline-flex items-center gap-0.5 text-[#666] text-xs font-mono"><Minus size={10} />0</span>;
  if (value > 0) return <span className="inline-flex items-center gap-0.5 text-[#4ade80] text-xs font-mono"><CaretUp size={12} weight="fill" />+{value}</span>;
  return <span className="inline-flex items-center gap-0.5 text-[#ff3b30] text-xs font-mono"><CaretDown size={12} weight="fill" />{value}</span>;
}

function MovementBadge({ badge }) {
  if (!badge) return null;
  const config = {
    "top-riser": { label: "TOP RISER", icon: TrendUp, color: "text-[#4ade80]", bg: "bg-[#4ade80]/10 border-[#4ade80]/30" },
    "rising": { label: "RISING", icon: TrendUp, color: "text-[#4ade80]", bg: "bg-[#4ade80]/10 border-[#4ade80]/30" },
    "top-faller": { label: "TOP FALLER", icon: TrendDown, color: "text-[#ff3b30]", bg: "bg-[#ff3b30]/10 border-[#ff3b30]/30" },
    "falling": { label: "FALLING", icon: TrendDown, color: "text-[#ff3b30]", bg: "bg-[#ff3b30]/10 border-[#ff3b30]/30" },
  };
  const c = config[badge];
  if (!c) return null;
  const Icon = c.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider border rounded-sm ${c.bg} ${c.color}`}><Icon size={10} weight="bold" />{c.label}</span>;
}

function VotePill({ label, count, total, active, onClick, colorHex }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <button
      onClick={onClick}
      data-testid={`vote-${label.toLowerCase()}`}
      className={`relative flex-1 py-3 px-2 border rounded-sm text-center transition-all overflow-hidden ${active ? "border-white/30 bg-white/5" : "border-[#262626] bg-[#171717] hover:border-[#444]"}`}
    >
      <div className="absolute bottom-0 left-0 right-0 opacity-15 rounded-sm transition-all" style={{ height: `${pct}%`, backgroundColor: colorHex }} />
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#888] relative z-10">{label}</p>
      <p className="font-mono text-sm font-bold text-white relative z-10">{pct}%</p>
      <p className="text-[9px] text-[#555] relative z-10">{count}</p>
    </button>
  );
}

function TradeCard({ trade }) {
  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
    catch { return ""; }
  };
  return (
    <div className="border border-[#262626] bg-[#171717] p-3 sm:p-4" data-testid="trade-card">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[9px] text-[#555] uppercase tracking-wider">{formatDate(trade.date)}</p>
        {trade.involving && <p className="text-[9px] text-[#ccff00] uppercase tracking-wider">Trade involving {trade.involving}</p>}
      </div>
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-1">
          <p className="text-[9px] text-[#666] uppercase tracking-wider font-semibold mb-1">SIDE A</p>
          {trade.side1.map((p, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {p.position === "PICK" ? <span className="text-[9px] px-1.5 py-0 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm font-bold">PICK</span> : <PosBadge pos={p.position} />}
              <Link to={p.position !== "PICK" ? `/players/${playerToSlug(p.name)}` : "#"} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link>
            </div>
          ))}
        </div>
        <div className="flex items-center self-center px-2"><ArrowsLeftRight size={14} className="text-[#444]" /></div>
        <div className="flex-1 space-y-1">
          <p className="text-[9px] text-[#666] uppercase tracking-wider font-semibold mb-1">SIDE B</p>
          {trade.side2.map((p, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {p.position === "PICK" ? <span className="text-[9px] px-1.5 py-0 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm font-bold">PICK</span> : <PosBadge pos={p.position} />}
              <Link to={p.position !== "PICK" ? `/players/${playerToSlug(p.name)}` : "#"} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DepthChartSection({ depthChart }) {
  const grouped = {};
  for (const p of depthChart) {
    if (!grouped[p.position]) grouped[p.position] = [];
    grouped[p.position].push(p);
  }
  return (
    <div className="space-y-3">
      {["QB", "RB", "WR", "TE"].map(pos => {
        const players = grouped[pos];
        if (!players?.length) return null;
        return (
          <div key={pos}>
            <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] mb-1.5">{pos}</p>
            <div className="space-y-1">
              {players.map((p, i) => (
                <Link key={i} to={`/players/${playerToSlug(p.name)}`} className={`flex items-center justify-between px-3 py-1.5 rounded-sm no-underline transition-colors ${p.isTarget ? "bg-[#ccff00]/10 border border-[#ccff00]/20" : "hover:bg-[#1a1a1a]"}`} data-testid={`depth-${pos.toLowerCase()}-${i}`}>
                  <span className={`text-xs font-medium ${p.isTarget ? "text-[#ccff00]" : "text-white"}`}>{p.name}</span>
                  <span className="text-xs font-mono text-[#666]">{p.value.toLocaleString()}</span>
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ValueCrossTab({ values }) {
  const formats = [
    { label: "PPR", sf: values.sf_ppr, oneqb: values.oneqb_ppr },
    { label: "Half PPR", sf: values.sf_halfppr, oneqb: null },
    { label: "Standard", sf: values.sf_std, oneqb: null },
  ].filter(f => f.sf > 0 || f.oneqb > 0);
  return (
    <div className="border border-[#262626] overflow-hidden" data-testid="value-cross-tab">
      <div className="grid grid-cols-3 bg-[#0a0a0a] px-3 py-2 border-b border-[#262626]">
        <span className="text-[9px] font-bold uppercase tracking-wider text-[#555]">Format</span>
        <span className="text-[9px] font-bold uppercase tracking-wider text-[#555] text-right">Superflex</span>
        <span className="text-[9px] font-bold uppercase tracking-wider text-[#555] text-right">1QB</span>
      </div>
      {formats.map(f => (
        <div key={f.label} className="grid grid-cols-3 px-3 py-2 border-b border-[#1a1a1a] bg-[#171717]">
          <span className="text-xs text-[#a3a3a3]">{f.label}</span>
          <span className="text-xs font-mono font-bold text-white text-right">{f.sf?.toLocaleString() || "—"}</span>
          <span className="text-xs font-mono text-[#a3a3a3] text-right">{f.oneqb?.toLocaleString() || "—"}</span>
        </div>
      ))}
    </div>
  );
}

/* --- Tier 2: AI Analysis Section --- */
function AnalysisSection({ playerSlug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/players/${encodeURIComponent(playerSlug)}/analysis`);
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [playerSlug]);

  if (loading) return <div className="h-32 skeleton rounded-sm" />;
  if (!data) return null;

  const projections = data.projections || {};
  const projData = [
    projections.year1 && { label: "1 Year", value: projections.year1.value, reasoning: projections.year1.reasoning },
    projections.year3 && { label: "3 Year", value: projections.year3.value, reasoning: projections.year3.reasoning },
    projections.year5 && { label: "5 Year", value: projections.year5.value, reasoning: projections.year5.reasoning },
  ].filter(Boolean);

  return (
    <div className="space-y-4" data-testid="ai-analysis-section">
      {/* Dynasty Outlook */}
      <div>
        <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><Brain size={12} /> DYNASTY OUTLOOK</h2>
        <div className="border border-[#262626] bg-[#171717] p-4 sm:p-5">
          <p className="text-sm text-[#c0c0c0] leading-relaxed whitespace-pre-line" data-testid="analysis-text">{data.analysis}</p>
          {data.cached && <p className="text-[9px] text-[#444] mt-3">Auto-generated analysis &middot; Updated every 3 days</p>}
        </div>
      </div>

      {/* Multi-Year Projections */}
      {projData.length > 0 && (
        <div data-testid="projections-section">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><ChartLine size={12} /> VALUE PROJECTIONS</h2>
          <div className="border border-[#262626] bg-[#171717] p-4">
            <div className="mb-3">
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={projData} barSize={40}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#888" }} axisLine={false} tickLine={false} />
                  <YAxis hide domain={[0, "auto"]} />
                  <Tooltip contentStyle={{ background: "#171717", border: "1px solid #262626", borderRadius: 2, fontSize: 11 }} labelStyle={{ color: "#888" }} />
                  <Bar dataKey="value" fill="#ccff00" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {projData.map(pr => (
                <div key={pr.label} className="flex items-center justify-between text-xs">
                  <span className="text-[#888]">{pr.label}</span>
                  <span className="font-mono font-bold text-white">{pr.value?.toLocaleString()}</span>
                  <span className="text-[10px] text-[#555] max-w-[50%] truncate">{pr.reasoning}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Tier 2: News/Market Activity Section --- */
function NewsSection({ playerSlug }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/players/${encodeURIComponent(playerSlug)}/news`);
        setNews(res.data.news || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [playerSlug]);

  if (loading) return <div className="h-20 skeleton rounded-sm" />;
  if (news.length === 0) return null;

  const severityColor = { positive: "border-l-[#4ade80]", negative: "border-l-[#ff3b30]", neutral: "border-l-[#555]" };

  return (
    <div data-testid="news-section">
      <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><Newspaper size={12} /> MARKET ACTIVITY</h2>
      <div className="space-y-2">
        {news.map((item, i) => (
          <div key={i} className={`border border-[#262626] bg-[#171717] p-3 border-l-2 ${severityColor[item.severity] || severityColor.neutral}`} data-testid={`news-item-${i}`}>
            <p className="text-xs font-semibold text-white mb-0.5">{item.title}</p>
            <p className="text-[10px] text-[#888]">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


/* --- Real Trades Section (from our Sleeper crawl) --- */
function RealTradesSection({ playerSlug, playerName }) {
  const [trades, setTrades] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/trades/player/${encodeURIComponent(playerSlug)}?limit=4`);
        setTrades(res.data.trades || []);
        setTotal(res.data.total_trades || 0);
      } catch (e) { /* trades not available yet */ }
      setLoading(false);
    })();
  }, [playerSlug]);

  if (loading) return <div className="h-24 skeleton rounded-sm" />;
  if (trades.length === 0) return null;

  return (
    <div data-testid="real-trades-section">
      <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><ArrowsLeftRight size={12} /> REAL TRADES ({total.toLocaleString()} total)</h2>
      <div className="space-y-3">
        {trades.slice(0, 4).map((t, i) => (
          <div key={i} className="border border-[#262626] bg-[#171717] p-3 sm:p-4" data-testid={`real-trade-${i}`}>
            <p className="text-[9px] text-[#555] uppercase tracking-wider mb-2">Week {t.week || "?"} — From {total.toLocaleString()} trades across {(total * 0.3).toFixed(0)}+ leagues</p>
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-1.5">
                <p className="text-[8px] text-[#555] uppercase tracking-wider font-bold">SIDE A GETS</p>
                {(t.side_a || []).map((p, j) => (
                  <div key={j} className="flex items-center gap-1.5">
                    {p.position === "PICK" ? <span className="text-[9px] px-1.5 py-0 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm font-bold">PICK</span> : <PosBadge pos={p.position} />}
                    {p.position !== "PICK" ? (
                      <Link to={`/players/${playerToSlug(p.name)}`} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link>
                    ) : (
                      <span className="text-xs text-white">{p.name}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center self-center px-2"><ArrowsLeftRight size={14} className="text-[#444]" /></div>
              <div className="flex-1 space-y-1.5">
                <p className="text-[8px] text-[#555] uppercase tracking-wider font-bold">SIDE B GETS</p>
                {(t.side_b || []).map((p, j) => (
                  <div key={j} className="flex items-center gap-1.5">
                    {p.position === "PICK" ? <span className="text-[9px] px-1.5 py-0 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm font-bold">PICK</span> : <PosBadge pos={p.position} />}
                    {p.position !== "PICK" ? (
                      <Link to={`/players/${playerToSlug(p.name)}`} className="text-xs text-white hover:text-[#ccff00] no-underline truncate">{p.name}</Link>
                    ) : (
                      <span className="text-xs text-white">{p.name}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {total > 4 && (
        <Link to={`/trades/${playerSlug}`} className="block mt-3 text-center text-[11px] text-[#ccff00] no-underline hover:underline" data-testid="see-all-trades">
          See all {total.toLocaleString()} trades for {playerName} →
        </Link>
      )}
    </div>
  );
}


/* --- Tier 3: Roster Context Banner (Signed-In Users) --- */
function RosterContextBanner({ playerSlug, profile }) {
  const [contexts, setContexts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/players/${encodeURIComponent(playerSlug)}/roster-context`, { withCredentials: true });
        if (res.data.status === "ok") {
          setContexts(res.data.contexts || []);
        }
      } catch (e) { /* not logged in */ }
      setLoading(false);
    })();
  }, [playerSlug]);

  if (loading || !contexts || contexts.length === 0) return null;

  return (
    <div className="mb-4 space-y-1" data-testid="roster-context">
      {contexts.map((ctx, i) => {
        if (ctx.location === "your_team") {
          return (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-[#ccff00]/10 border border-[#ccff00]/30 rounded-sm">
              <UserCircle size={14} className="text-[#ccff00]" weight="fill" />
              <p className="text-xs text-[#ccff00] font-semibold">{profile.name} is on YOUR team in {ctx.league}</p>
            </div>
          );
        }
        if (ctx.location === "league_mate") {
          return (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-[#45b7d1]/10 border border-[#45b7d1]/30 rounded-sm">
              <Target size={14} className="text-[#45b7d1]" />
              <p className="text-xs text-[#45b7d1] font-semibold">{profile.name} is on a league-mate's team in {ctx.league}</p>
            </div>
          );
        }
        if (ctx.location === "free_agent") {
          return (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-sm">
              <Crosshair size={14} className="text-[#4ade80]" />
              <p className="text-xs text-[#4ade80] font-semibold">{profile.name} is a FREE AGENT in {ctx.league}</p>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

/* --- Tier 3: AI Trade Verdict (Signed-In Users) --- */
function AITradeVerdict({ playerSlug }) {
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authState, setAuthState] = useState(null); // 'logged_in', 'anonymous', 'no_credits'
  const [errorMsg, setErrorMsg] = useState(null);

  const fetchVerdict = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await axios.post(`${API}/players/${encodeURIComponent(playerSlug)}/ai-trade-verdict`, {}, { withCredentials: true });
      if (res.data.status === "requires_login") {
        setAuthState("anonymous");
      } else if (res.data.status === "no_credits") {
        setAuthState("no_credits");
        setErrorMsg(res.data.message);
      } else if (res.data.status === "no_league") {
        setErrorMsg(res.data.verdict);
        setAuthState("logged_in");
      } else {
        setAuthState("logged_in");
        setVerdict(res.data);
      }
    } catch (e) {
      setAuthState("anonymous");
    }
    setLoading(false);
  };

  // Check auth status on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (res.data?.email || res.data?.sleeper_username) {
          setAuthState("logged_in");
        } else {
          setAuthState("anonymous");
        }
      } catch { setAuthState("anonymous"); }
    })();
  }, []);

  const handleGoogleLogin = () => {
    const r = window.location.origin + window.location.pathname;
    window.location.href = '/api/auth/google/login';
  };

  const verdictColors = {
    BUY: { bg: "bg-[#4ade80]/10", border: "border-[#4ade80]/30", text: "text-[#4ade80]" },
    SELL: { bg: "bg-[#ff3b30]/10", border: "border-[#ff3b30]/30", text: "text-[#ff3b30]" },
    HOLD: { bg: "bg-[#fbbf24]/10", border: "border-[#fbbf24]/30", text: "text-[#fbbf24]" },
    AVOID: { bg: "bg-[#ff3b30]/10", border: "border-[#ff3b30]/30", text: "text-[#ff3b30]" },
  };

  return (
    <div data-testid="ai-trade-verdict">
      <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><Brain size={12} weight="fill" /> AI TRADE VERDICT</h2>

      {verdict ? (
        <div className={`border ${verdictColors[verdict.verdict]?.border || "border-[#262626]"} ${verdictColors[verdict.verdict]?.bg || "bg-[#171717]"} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`font-heading text-xl font-bold uppercase ${verdictColors[verdict.verdict]?.text || "text-white"}`} data-testid="verdict-label">{verdict.verdict}</span>
            {verdict.confidence && <span className="text-[10px] text-[#888]">Confidence: {verdict.confidence}/10</span>}
          </div>
          <p className="text-xs text-[#c0c0c0] mb-3" data-testid="verdict-summary">{verdict.summary}</p>
          {verdict.trade_idea && (
            <div className="border-t border-white/10 pt-2 mt-2">
              <p className="text-[9px] text-[#666] uppercase tracking-wider font-bold mb-1">TRADE IDEA</p>
              <p className="text-xs text-[#a3a3a3]" data-testid="verdict-trade-idea">{verdict.trade_idea}</p>
            </div>
          )}
        </div>
      ) : authState === "anonymous" ? (
        <div className="border border-[#262626] bg-[#171717] p-4">
          <Brain size={16} className="text-[#ccff00] mb-2" weight="fill" />
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#ccff00] mb-2">AI TRADE VERDICT</p>
          <p className="text-xs text-[#a3a3a3] mb-3">Import your Sleeper league to get a personalized verdict: should you trade for this player given YOUR actual roster? The AI analyzes your team's strengths, weaknesses, and contender status to give a specific BUY / HOLD / SELL / AVOID recommendation with a trade package suggestion.</p>
          <div className="bg-[#0a0a0a] border border-[#262626] p-3 mb-3 rounded-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[9px] px-2 py-0.5 bg-[#4ade80]/10 border border-[#4ade80]/30 text-[#4ade80] rounded-sm font-bold">SAMPLE</span>
              <span className="text-[10px] text-[#555]">What a verdict looks like</span>
            </div>
            <p className="text-xs text-[#888] italic">"BUY — Your roster is short on RB depth and you have WR capital to spare. Consider packaging a WR2 + mid-round pick."</p>
          </div>
          <button onClick={handleGoogleLogin} className="w-full h-8 flex items-center justify-center gap-1.5 bg-white text-black font-bold text-[10px] rounded-sm hover:bg-gray-100 transition-colors" data-testid="signin-for-verdict">
            <GoogleLogo size={12} weight="bold" /> SIGN IN FOR YOUR PERSONALIZED VERDICT
          </button>
          <p className="text-[9px] text-[#555] text-center mt-2">Free — 30 credits included, no payment required</p>
        </div>
      ) : authState === "no_credits" ? (
        <div className="border border-[#262626] bg-[#171717] p-4 text-center">
          <Lock size={18} className="text-[#555] mx-auto mb-2" />
          <p className="text-xs text-[#888] mb-2">{errorMsg || "Free credits used up"}</p>
          <Link to="/billing" className="inline-flex items-center gap-1.5 h-8 px-4 bg-[#ccff00] text-black font-bold text-[10px] rounded-sm no-underline hover:bg-[#b8e600] transition-colors" data-testid="upgrade-for-verdict">
            <Lightning size={12} weight="fill" /> UPGRADE TO PRO
          </Link>
        </div>
      ) : (
        <>
          <button
            onClick={fetchVerdict}
            disabled={loading}
            className="w-full h-10 flex items-center justify-center gap-2 border border-[#ccff00]/50 bg-[#ccff00]/5 text-[#ccff00] font-bold text-xs uppercase tracking-wider rounded-sm hover:bg-[#ccff00]/10 transition-colors disabled:opacity-50"
            data-testid="ask-ai-verdict"
          >
            {loading ? (
              <><div className="w-3 h-3 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin" /> Analyzing your roster...</>
            ) : (
              <><Brain size={14} weight="fill" /> Should I trade for this player?</>
            )}
          </button>
          {errorMsg && <p className="text-[10px] text-[#888] text-center mt-2">{errorMsg}</p>}
        </>
      )}
    </div>
  );
}

/* --- Main Component --- */
export default function PlayerPage() {
  const { name } = useParams();
  const playerSlug = name || "";

  // Use preloaded data from build-time injection if available (prevents "Player not found" during SSR/bot render)
  const preloaded = typeof window !== "undefined" && window.__PLAYER_DATA__?.slug === playerSlug ? window.__PLAYER_DATA__ : null;

  const [profile, setProfile] = useState(preloaded ? {
    ...preloaded,
    values: preloaded.values || {},
    ranks: preloaded.ranks || {},
    similarPlayers: preloaded.similarPlayers || [],
    depthChart: (preloaded.depthChart || []).map(dp => ({ ...dp, isTarget: dp.isTarget || dp.slug === playerSlug })),
    votes: { buy: 0, hold: 0, sell: 0, total: 0 },
  } : null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(!preloaded);
  const [voteSent, setVoteSent] = useState(null);
  const [votes, setVotes] = useState({ buy: 0, hold: 0, sell: 0, total: 0 });

  const fetchProfile = useCallback(async () => {
    if (!preloaded) setLoading(true);
    try {
      const res = await axios.get(`${API}/players/${encodeURIComponent(playerSlug)}/profile`);
      setProfile(res.data);
      setVotes(res.data.votes || { buy: 0, hold: 0, sell: 0, total: 0 });
      trackEvent("player_page_view", playerSlug);
    } catch (e) {
      console.error(e);
      // If API fails but we have preloaded data, keep showing it
      if (!preloaded) setProfile(null);
    }
    setLoading(false);
  }, [playerSlug, preloaded]);


  useEffect(() => { if (playerSlug) { fetchProfile(); } }, [playerSlug, fetchProfile]);

  const handleVote = async (vote) => {
    if (voteSent) return;
    try {
      const res = await axios.post(`${API}/players/${encodeURIComponent(playerSlug)}/vote`, { vote });
      if (res.data.votes) {
        const v = res.data.votes;
        setVotes({ ...v, total: v.buy + v.hold + v.sell });
      }
      setVoteSent(vote);
      trackEvent("player_vote", `${playerSlug}:${vote}`);
    } catch (e) { console.error(e); }
  };

  const handleGoogleLogin = () => {
    const r = window.location.origin + "/";
    window.location.href = '/api/auth/google/login';
  };

  if (loading) {
    const displayName = playerSlug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <SEO title={`${displayName} Dynasty Trade Value & Rankings — Dynasty Trade Maker`} description={`${displayName} dynasty player value, rankings, and trade analysis.`} path={`/players/${playerSlug}`} />
        <PublicNav />
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
          <div className="h-8 w-48 skeleton rounded-sm" />
          <div className="h-40 skeleton rounded-sm" />
          <div className="grid grid-cols-2 gap-4"><div className="h-24 skeleton rounded-sm" /><div className="h-24 skeleton rounded-sm" /></div>
          <div className="grid grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 skeleton rounded-sm" />)}</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    const displayName = playerSlug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <SEO title={`${displayName} — Dynasty Trade Maker`} description={`${displayName} dynasty fantasy football player profile.`} path={`/players/${playerSlug}`} />
        <PublicNav />
        <div className="max-w-5xl mx-auto px-4 py-20 text-center">
          <p className="text-[#a3a3a3] text-lg mb-4">Player not found</p>
          <Link to="/rankings" className="text-[#ccff00] text-sm no-underline hover:underline">Browse all rankings</Link>
        </div>
      </div>
    );
  }

  const p = profile;
  const sfPremium = (p.values.sf_ppr || 0) - (p.values.oneqb_ppr || 0);
  const playerImg = p.sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${p.sleeperId}.jpg` : null;
  const simNames = (p.similarPlayers || []).slice(0, 3).map(s => s.name).join(", ");
  const playerFaqs = [
    { q: `What is ${p.name}'s dynasty trade value?`, a: `${p.name} has a Superflex dynasty value of ${p.values.sf_ppr?.toLocaleString() || "—"}${p.ranks.overall_sf ? ` (overall #${p.ranks.overall_sf}${p.ranks.position_sf ? `, ${p.position} #${p.ranks.position_sf}` : ""})` : ""}, based on FantasyCalc consensus data from 150,000+ real dynasty leagues, updated daily.` },
    { q: `Is ${p.name} a good dynasty asset right now?`, a: `${p.name} is a${p.tier ? ` Tier ${p.tier}` : ""} dynasty ${p.position}${p.age ? ` at age ${p.age}` : ""}${p.team ? ` on ${p.team}` : ""}. Whether to buy, hold, or sell depends on your contention window — the value trends and real trades on this page show how the market is treating him.` },
    { q: `What should I expect in return when trading ${p.name}?`, a: simNames ? `Players with similar dynasty value include ${simNames}. Use the free trade calculator to test exact packages against live market values.` : `Use the free trade calculator to test packages against live market values from real dynasty leagues.` },
    { q: `How is ${p.name}'s dynasty value calculated?`, a: `Values come from FantasyCalc's consensus of real dynasty trades and markets across 150,000+ Sleeper leagues, refreshed daily — it reflects what managers actually pay, not one analyst's opinion.` },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title={`${p.name} Dynasty Trade Value & Rankings — Dynasty Trade Maker`}
        description={`${p.name} (${p.position} — ${p.team || "FA"}) dynasty value: SF ${p.values.sf_ppr?.toLocaleString()}, 1QB ${p.values.oneqb_ppr?.toLocaleString()}. Tier ${p.tier}. Age ${p.age}. ${p.college}. Trade value chart, similar players, and real trades.`}
        path={`/players/${playerToSlug(p.name)}`}
      />
      <BreadcrumbLd items={[{ name: "Home", url: "/" }, { name: "Players", url: "/players" }, { name: p.name, url: `/players/${playerToSlug(p.name)}` }]} />
      <FaqLd faqs={playerFaqs} />
      <PublicNav />

      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        {/* Breadcrumb */}
        <Link to="/rankings" className="inline-flex items-center gap-1 text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline mb-4 uppercase tracking-wider" data-testid="back-to-rankings">
          <ArrowLeft size={10} /> Rankings
        </Link>

        {/* Tier 3: Roster Context (Pro Only) */}
        <RosterContextBanner playerSlug={playerSlug} profile={p} />

        {/* Hero Header */}
        <div className="border border-[#262626] bg-[#171717] p-5 sm:p-8 mb-6" data-testid="player-hero">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
            {playerImg && (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-sm overflow-hidden border border-[#262626] bg-[#0a0a0a] flex-shrink-0">
                <img src={playerImg} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <PosBadge pos={p.position} />
                <MovementBadge badge={p.badge} />
                {p.injuryStatus && <span className="text-[9px] px-2 py-0.5 bg-[#ff3b30]/10 border border-[#ff3b30]/30 text-[#ff3b30] rounded-sm font-bold uppercase">{p.injuryStatus}</span>}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white uppercase leading-none mb-3" data-testid="player-name">{p.name}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#888]">
                <span className="font-medium text-[#a3a3a3]">{p.team || "Free Agent"}</span>
                {p.number && <span>#{p.number}</span>}
                {p.age && <span>{p.age} yrs</span>}
                {p.height && <span>{p.height}</span>}
                {p.weight && <span>{p.weight} lbs</span>}
                {p.college && <span>{p.college}</span>}
                {p.yoe != null && <span>{p.yoe} yr exp</span>}
                {p.draftYear && <span>Draft: {p.draftYear} Rd {p.draftRound} Pick {p.draftPick}</span>}
                {p.birthDate && <span>DOB: {new Date(p.birthDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}
              </div>
            </div>
            <div className="flex sm:flex-col gap-3 sm:gap-2 flex-shrink-0">
              <div className="text-center px-3 py-2 border border-[#262626] bg-[#0a0a0a] rounded-sm min-w-[70px]">
                <p className="text-[8px] font-bold uppercase tracking-wider text-[#555]">Overall</p>
                <p className="font-mono font-bold text-lg text-[#ccff00]">#{p.ranks.overall_sf || "—"}</p>
              </div>
              <div className="text-center px-3 py-2 border border-[#262626] bg-[#0a0a0a] rounded-sm min-w-[70px]">
                <p className="text-[8px] font-bold uppercase tracking-wider text-[#555]">{p.position}</p>
                <p className="font-mono font-bold text-lg text-white">#{p.ranks.position_sf || "—"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content (2/3) */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Value Cards — more compact on mobile */}
            <div className="grid grid-cols-2 gap-3" data-testid="value-cards">
              <div className="border border-[#ccff00]/30 bg-[#171717] p-3 sm:p-5 text-center">
                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#555] mb-0.5">SUPERFLEX VALUE</p>
                <p className="font-mono font-bold text-xl sm:text-3xl text-[#ccff00]" data-testid="sf-value">{p.values.sf_ppr?.toLocaleString() || "—"}</p>
                <div className="mt-0.5"><TrendBadge value={p.trend30Day} /></div>
              </div>
              <div className="border border-[#262626] bg-[#171717] p-3 sm:p-5 text-center">
                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#555] mb-0.5">1QB VALUE</p>
                <p className="font-mono font-bold text-xl sm:text-3xl text-white" data-testid="oneqb-value">{p.values.oneqb_ppr?.toLocaleString() || "—"}</p>
                <p className="text-[10px] text-[#555] mt-0.5">Rank #{p.ranks.overall_1qb || "—"}</p>
              </div>
            </div>

            {/* Compact stats row on mobile (inline instead of grid boxes) */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 sm:hidden text-[11px] px-1">
              <span className="text-[#888]">Tier <strong className="text-white">{p.tier || "—"}</strong></span>
              {p.redraftValue > 0 && <span className="text-[#888]">Redraft <strong className="text-white">{p.redraftValue.toLocaleString()}</strong></span>}
              {p.tradeFrequency > 0 && <span className="text-[#888]">Trade Freq <strong className="text-white">{(p.tradeFrequency * 100).toFixed(1)}%</strong></span>}
              {sfPremium !== 0 && <span className="text-[#888]">SF Premium <strong className="text-white">{sfPremium > 0 ? `+${sfPremium.toLocaleString()}` : sfPremium.toLocaleString()}</strong></span>}
            </div>

            {/* Desktop stats grid (hidden on mobile) */}
            <div className="hidden sm:grid grid-cols-4 gap-3" data-testid="stats-grid">
              <StatBox label="Tier" value={p.tier || "—"} />
              {p.redraftValue > 0 && <StatBox label="Redraft Value" value={p.redraftValue.toLocaleString()} />}
              {p.tradeFrequency > 0 && <StatBox label="Trade Frequency" value={`${(p.tradeFrequency * 100).toFixed(1)}%`} />}
              {sfPremium !== 0 && <StatBox label="SF Premium" value={sfPremium > 0 ? `+${sfPremium.toLocaleString()}` : sfPremium.toLocaleString()} />}
            </div>

            {/* Championship Badge */}
            {p.championship && (
              <Link to="/championship-blueprint" className="flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border border-[#ccff00]/30 bg-[#ccff00]/5 no-underline rounded-sm transition-colors hover:bg-[#ccff00]/10" data-testid="championship-badge">
                <Trophy size={18} className="text-[#ccff00] shrink-0" weight="fill" />
                <div>
                  <p className="text-[12px] sm:text-sm text-white font-bold">{p.championship.pct}% of dynasty champions had {p.name}</p>
                  <p className="text-[10px] text-[#888]">{p.championship.count.toLocaleString()} of {p.championship.total.toLocaleString()} championship rosters</p>
                </div>
              </Link>
            )}

            {/* Dynasty Outlook — MOVED UP on mobile for SEO prominence */}
            <AnalysisSection playerSlug={playerSlug} />

            {/* Value Cross-Tab */}
            <div>
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><Scales size={12} /> VALUE BY SCORING FORMAT</h2>
              <ValueCrossTab values={p.values} />
            </div>

            {/* Tier 2: Market Activity / News */}
            <NewsSection playerSlug={playerSlug} />

            {/* Real Trades from our Sleeper dataset */}
            <RealTradesSection playerSlug={playerSlug} playerName={p.name} />

            {/* Trade CTA */}
            <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-5 sm:p-6" data-testid="trade-cta">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-heading text-lg text-white uppercase mb-1">TRADE {p.name.split(" ").pop().toUpperCase()}?</h3>
                  <p className="text-xs text-[#888]">Check what {p.name} is worth with our free calculator, or get AI-powered trade packages for your league.</p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-2 flex-shrink-0">
                  <Link to="/trade-calculator" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold" data-testid="trade-calc-cta">
                    <Lightning size={12} weight="fill" /> TRADE CALCULATOR
                  </Link>
                  <Link to="/features/trade-analyzer" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1" data-testid="ai-analyzer-cta">
                    AI Analyzer <ArrowRight size={11} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar (1/3) */}
          <div className="space-y-6">
            {/* Tier 3: AI Trade Verdict */}
            <AITradeVerdict playerSlug={playerSlug} />

            {/* Community Vote */}
            <div data-testid="community-vote">
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><Users size={12} /> COMMUNITY PULSE</h2>
              <div className="flex gap-2">
                <VotePill label="Buy" count={votes.buy} total={votes.total} active={voteSent === "buy"} onClick={() => handleVote("buy")} colorHex="#4ade80" />
                <VotePill label="Hold" count={votes.hold} total={votes.total} active={voteSent === "hold"} onClick={() => handleVote("hold")} colorHex="#fbbf24" />
                <VotePill label="Sell" count={votes.sell} total={votes.total} active={voteSent === "sell"} onClick={() => handleVote("sell")} colorHex="#ff3b30" />
              </div>
              {voteSent && <p className="text-[9px] text-[#555] text-center mt-2">Thanks for voting!</p>}
              {!voteSent && votes.total === 0 && <p className="text-[9px] text-[#555] text-center mt-2">Be the first to vote</p>}
            </div>

            {/* Similar Players */}
            {p.similarPlayers?.length > 0 && (
              <div data-testid="similar-players">
                <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><Scales size={12} /> VALUE ADJACENT</h2>
                <div className="border border-[#262626] bg-[#171717] divide-y divide-[#1a1a1a]">
                  {p.similarPlayers.slice(0, 8).map((sp, i) => (
                    <Link key={i} to={`/players/${playerToSlug(sp.name)}`} className="flex items-center justify-between px-3 py-2.5 hover:bg-[#1e1e1e] no-underline transition-colors" data-testid={`similar-player-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <PosBadge pos={sp.position} />
                        <div className="min-w-0">
                          <p className="text-xs text-white font-medium truncate">{sp.name}</p>
                          <p className="text-[10px] text-[#555]">{sp.team} &middot; #{sp.rank}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="text-xs font-mono font-bold text-white">{sp.value.toLocaleString()}</p>
                        <TrendBadge value={sp.trend} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Team Depth Chart */}
            {p.depthChart?.length > 0 && (
              <div data-testid="depth-chart">
                <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><Trophy size={12} /> {p.team} DEPTH CHART</h2>
                <div className="border border-[#262626] bg-[#171717] p-3">
                  <DepthChartSection depthChart={p.depthChart} />
                </div>
              </div>
            )}

            {/* Deep-Link Tools */}
            <div data-testid="tool-links">
              <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><Strategy size={12} /> DYNASTY TOOLS</h2>
              <div className="space-y-2">
                <Link to={`/draft-room?target=${encodeURIComponent(p.name)}`} className="flex items-center justify-between px-3 py-2.5 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline transition-colors rounded-sm" data-testid="link-draft-room">
                  <div className="flex items-center gap-2">
                    <GameController size={14} className="text-[#ccff00]" />
                    <span className="text-xs text-[#a3a3a3]">Draft War Room</span>
                  </div>
                  <span className="text-[9px] text-[#555]">Who to draft</span>
                </Link>
                <Link to={`/optimizer?target=${encodeURIComponent(p.name)}`} className="flex items-center justify-between px-3 py-2.5 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline transition-colors rounded-sm" data-testid="link-optimizer">
                  <div className="flex items-center gap-2">
                    <Target size={14} className="text-[#ccff00]" />
                    <span className="text-xs text-[#a3a3a3]">Roster Optimizer</span>
                  </div>
                  <span className="text-[9px] text-[#555]">Build around {p.name.split(" ").pop()}</span>
                </Link>
                <Link to={`/trade-calculator`} className="flex items-center justify-between px-3 py-2.5 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline transition-colors rounded-sm" data-testid="link-calculator">
                  <div className="flex items-center gap-2">
                    <Scales size={14} className="text-[#ccff00]" />
                    <span className="text-xs text-[#a3a3a3]">Trade Calculator</span>
                  </div>
                  <span className="text-[9px] text-[#555]">Check value</span>
                </Link>
                <Link to={`/trades/${playerSlug}`} className="flex items-center justify-between px-3 py-2.5 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline transition-colors rounded-sm" data-testid="link-real-trades">
                  <div className="flex items-center gap-2">
                    <ArrowsLeftRight size={14} className="text-[#ccff00]" />
                    <span className="text-xs text-[#a3a3a3]">Real Trades</span>
                  </div>
                  <span className="text-[9px] text-[#555]">See actual deals</span>
                </Link>
              </div>
            </div>

            {/* Quick Compare */}
            {p.similarPlayers?.length > 0 && (
              <div data-testid="quick-compare">
                <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5"><ArrowsLeftRight size={12} /> HEAD-TO-HEAD COMPARISONS</h2>
                <div className="space-y-1.5">
                  {p.similarPlayers.filter(sp => sp.position === p.position).slice(0, 4).map((sp, i) => (
                    <Link key={i} to={compareUrl(p.name, sp.name)} className="flex items-center justify-between px-3 py-2 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline transition-colors rounded-sm" data-testid={`compare-${i}`}>
                      <span className="text-xs text-[#a3a3a3]">{p.name.split(" ").pop()} vs {sp.name.split(" ").pop()}</span>
                      <ArrowRight size={11} className="text-[#555]" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Sign Up CTA */}
            <div className="border border-[#262626] bg-[#171717] p-5 text-center" data-testid="signup-cta">
              <Fire size={20} className="text-[#ccff00] mx-auto mb-2" weight="fill" />
              <p className="text-xs text-white font-bold mb-1 uppercase">Import Your League</p>
              <p className="text-[10px] text-[#666] mb-3">See if {p.name} fits your roster. Get personalized trade suggestions.</p>
              <button onClick={handleGoogleLogin} className="w-full h-9 flex items-center justify-center gap-1.5 bg-white text-black font-bold text-[10px] rounded-sm hover:bg-gray-100 transition-colors" data-testid="google-signin-cta">
                <GoogleLogo size={12} weight="bold" /> SIGN IN WITH GOOGLE
              </button>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-10 pt-6 border-t border-[#1a1a1a]" data-testid="player-faq-section">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {playerFaqs.map((f, i) => (
              <div key={i}>
                <p className="text-xs font-bold text-white mb-1">{f.q}</p>
                <p className="text-xs text-[#888] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-10 pt-6 border-t border-[#1a1a1a] text-center text-[10px] text-[#444] space-x-3">
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/rookie-rankings" className="hover:text-[#a3a3a3] no-underline">Rookies</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/features" className="hover:text-[#a3a3a3] no-underline">Features</Link>
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</Link>
          <span>dynastytrademaker.com</span>
        </div>
      </div>
    </div>
  );
}

function PlayerNav() {
  return (
    <div className="border-b border-[#262626] sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <div className="w-7 h-7 bg-[#ccff00] flex items-center justify-center rounded-sm"><Football size={14} weight="bold" color="#000" /></div>
          <span className="font-heading text-xl text-white">DYNASTY TRADE<span className="text-[#ccff00]">MAKER</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/trade-calculator" className="text-xs text-[#ccff00] hover:text-white no-underline font-bold hidden sm:block">Free Calculator</Link>
          <Link to="/rankings" className="text-xs text-[#a3a3a3] hover:text-white no-underline hidden sm:block">Rankings</Link>
          <Link to="/rookie-rankings" className="text-xs text-[#a3a3a3] hover:text-white no-underline hidden sm:block">Rookies</Link>
        </div>
      </div>
    </div>
  );
}
